import React, { useEffect, useState, useRef } from 'react';

/**
 * SociableKitWidget Component
 * 
 * Loads and displays a LinkedIn profile feed widget using the SociableKit third-party service.
 * Handles loading, ready, and error states. Includes timeout fallback if widget fails to load
 * within 5 seconds and monitors DOM for successful widget injection.
 * 
 * @returns {JSX.Element} Container for LinkedIn profile feed widget with status messages
 */
const SociableKitWidget = () => {
    // Widget loading state: 'loading' (initial), 'ready' (loaded), or 'error' (failed)
    const [status, setStatus] = useState('loading');

    // Reference to DOM container where widget will be injected
    const containerRef = useRef(null);

    // SociableKit widget ID for LinkedIn profile feed
    const widgetId = '25617037';

    useEffect(() => {
        const scriptSrc = 'https://widgets.sociablekit.com/linkedin-profile-posts/widget.js';

        // Check if script is already loaded to avoid duplicates
        let script = document.querySelector(`script[src="${scriptSrc}"]`);

        // Callback when script fails to load
        const handleScriptError = () => {
            console.error("SociableKit script failed to load.");
            setStatus('error');
        };

        // Load the SociableKit script if not already present
        if (!script) {
            script = document.createElement('script');
            script.src = scriptSrc;
            script.defer = true;
            script.onerror = handleScriptError;
            document.body.appendChild(script);
        }

        // Set 5-second timeout for widget to render content
        // If no content appears within 5 seconds, mark as error
        const timeout = setTimeout(() => {
            if (containerRef.current && containerRef.current.innerHTML === '') {
                console.warn("Widget timeout: No content rendered.");
                setStatus('error');
            }
        }, 5000);

        // Watch for DOM changes indicating successful widget injection
        const observer = new MutationObserver((mutations) => {
            // When widget content appears in container, mark as ready
            if (containerRef.current && containerRef.current.children.length > 0) {
                setStatus('ready');
                clearTimeout(timeout);
                observer.disconnect();
            }
        });

        // Start observing the container for child element changes
        if (containerRef.current) {
            observer.observe(containerRef.current, { childList: true });
        }

        // Cleanup: clear timeout and stop observer on unmount
        return () => {
            clearTimeout(timeout);
            observer.disconnect();
        };
    }, []);

    return (
        <div>
            {/* Show loading message while widget content is being loaded */}
            {status === 'loading' && <p>Loading feed...</p>}

            {/* Show error message if widget fails to load */}
            {status === 'error' && (
                <div style={{ color: 'red', padding: '10px', border: '1px solid red' }}>
                    <p>Failed to load LinkedIn feed. Please refresh or check back later.</p>
                </div>
            )}

            {/* Container where SociableKit will inject the LinkedIn feed widget */}
            <div
                ref={containerRef}
                className='sk-ww-linkedin-profile-post'
                data-embed-id={widgetId}
            ></div>
        </div>
    );
};

export default SociableKitWidget;